<template lang="pug">
div(v-if="catalogData" class="bg-white")
    SearchWrapper(:searchContext="$search.catalog")
        SearchResults

            Storyblok(
                :story="catalogData.story"
                sectionKey="topSections"
            )

        div(class="p-3")
            div(class="lg:container")
                Crumbs(
                    v-if="catalogData.crumbs"
                    :crumbs="catalogData.crumbs"
                )

                div(class="overflow-auto")
                    FitmentInline(
                        class="mt-4 -mx-3 lg:mt-6 lg:mx-0 bg-white lg:bg-gray-lighter"
                        :class="isCategoryFilterAB ? 'mb-4 lg:mb-6' : ''"
                    )

                h1(v-if="!disableH1" id="catalog-title" class="text-center sm:text-left" data-testid="catalogTitle")
                    span(
                        v-if="fitmentFormatted"
                        class="block text-gray-dark text-xl"
                    ) {{ fitmentFormatted }}&nbsp;
                    span(class="block" v-html="h1")

        div(
            v-if="!hideProducts"
            class="px-3 pb-6"
        )
            div(class="lg:container")
                div(ref="scrollTop" class="flex flex-wrap -mx-3")

                    //- A/B Category filter update - do not show Refinements overlay in test
                    div(
                        v-if="!isCategoryFilterAB"
                        class="px-3 w-full lg:w-1/5"
                    )
                        Refinements()

                    div(class="px-3 flex-1")

                        //- A/B Category filter update - hide original in test
                        template(v-if="!isCategoryFilterAB")
                            RefinementSelections(v-if="$device.isDesktop")

                            //- On Desktop we only want the toolbar to show when we have results
                            template(v-if="$device.isDesktop")
                                SearchResults
                                    Toolbar

                            template(v-else)
                                Toolbar


                        //- A/B Category filter update - RefinementSelections location changes based on device
                        //- Because of flex layout issues duplicating this section and v-if for desktop & mobile was easier
                        template(v-else)
                            RefinementSelections(v-if="$device.isSmall")

                            //- On Desktop we only want the toolbar to show when we have results
                            template(v-if="$device.isDesktop")
                                SearchResults
                                    Toolbar(:isCategoryFilterAB="isCategoryFilterAB")

                            template(v-else)
                                Toolbar(:isCategoryFilterAB="isCategoryFilterAB")

                            RefinementSelections(v-if="$device.isDesktop")


                        SearchResults(
                            class="flex flex-wrap -mx-2"
                            :class="isCategoryFilterAB ? 'lg:-mx-7' : 'lg:-mx-3'" data-testid="catalogResult"
                        )
                            template(#result="{ result, index }")
                                div(
                                    class="px-2 py-2 w-full xs:w-1/2 lg:py-0 "
                                    :class="isCategoryFilterAB ? 'lg:w-1/4 lg:px-7 lg:pb-14' : 'lg:w-1/3 lg:px-3' "
                                    :key="result.objectID"
                                )
                                    ProductListing(
                                        :listing="result"
                                        @productClicked="emitProductListClicked(result, index)"
                                        :isLazy="index >= 6"
                                    )

                        SearchNoResults
                            NoResults

                        SearchPagination

        SearchResults
            Storyblok(
                :story="catalogData.story"
                sectionKey="bottomSections"
                :forceLazyLoad="!hideProducts"
            )

        div( class="px-3 bg-white")
            div(class="lg:container")
                //- RECOMMENDED PRODUCTS
                CarouselProduct(v-if="$sitewideData.config.recommendedProductsEnabled" productType='recommended')
                //- RECENTLY VIEWED
                CarouselProduct(productType='recent')
</template>

<script>
import SearchWrapper from '@/components/search/wrapper'
import SearchResults from '@/components/search/results'
import SearchNoResults from '@/components/search/noResults'
import SearchPagination from '@/components/search/pagination'
import FitmentInline from '@/components/fitment/fitmentInline'
import Crumbs from '@/components/crumbs'
import ProductListing from '@/components/productListing'
import CarouselProduct from '@/components/carousel/product/'
import { linkBuilders } from '@/helpers/mixins'
import Storyblok from '@/integrations/storyblok'
import NoResults from './_components/noResults'
import Refinements from './_components/refinements'
import Toolbar from './_components/toolbar'
import RefinementSelections from './_components/refinementSelections'
import { updateSearch } from './_helpers/algolia'

export default {
    name: 'Catalog',
    components: {
        SearchWrapper,
        SearchResults,
        SearchNoResults,
        SearchPagination,
        FitmentInline,
        Crumbs,
        ProductListing,
        CarouselProduct,
        ImageLink: () => import(/* webpackChunkName: "imageLink" */ '@/components/imageLink'),
        NoResults,
        Refinements,
        Toolbar,
        RefinementSelections,
        Storyblok,
    },
    mixins: [linkBuilders],
    beforeRouteLeave(to, from, next) {
        // Only reset the fitment if navigating to a page that does not have custom fitment
        if (to.matched.some((record) => record.meta.hasCustomFitment)) {
            next()
            return
        }

        // Reset the fitment if the sameDayShipping feature is enabled.
        if (this.$sitewideData.config.sameDayShippingEnabled) {
            this.$store.dispatch('fitment/setupFitment').then(() => {
                this.$store.dispatch('fitment/initSelects')
            })
        }

        next()
    },
    middleware({ store, route, from }) {
        // -- Making an Else If for Clarity --
        // We want to early out if are simply filtering our current result set
        // and we don't need to run the initCatalog again.
        if (process.client && from && route) {
            if (route.name === 'search') {
                // Do not initCatalog if the Search Query has not changed.
                // As the data set will be the same just filtered.
                if (route.query.q === from.query.q) return
                // Do not initCatalog if the Category has not changed
                // As the data set will be the same just filtered.
            } else if (route.path === from.path) return
        }
        return store.dispatch('catalog/initCatalog', route)
    },
    async asyncData({ app, $device, store, route, error, $storyblok, $timing }) {
        const returnData = {
            // We need to skip initSelect on first hit because default layout does this for us already
            // so this is a data prop that lets us skip it.
            isFirstHit: process.server,
            asyncDataSuccess: false,
        }

        if (store.state.catalog.data === null) {
            error({ statusCode: 404, message: 'Item not found' })
            return returnData
        }

        // If the sameDayShipping feature is enabled then we want to do the setupFitment logic
        if (app.$sitewideData.config.sameDayShippingEnabled) {
            const { showSameDayShippingFacet } = store.state.catalog.data

            // if we are supposed to show the sameDayShipping facet, we need to pass the route so it knows what fitment it needs
            // otherwise we need to reset to the site defaults so we'd pass undefined instead
            await store.dispatch(
                'fitment/setupFitment',
                showSameDayShippingFacet ? route : undefined
            )
        }

        try {
            $timing.start('catalog-algolia')

            // If we have story data, check it for algolia searches
            const searchPromises = [updateSearch(app, store, route)]

            if (store.state.catalog.data.story)
                searchPromises.push(
                    $storyblok.fetchAlgoliaData(store.state.catalog.data.story, [
                        'topSections',
                        'bottomSections',
                    ])
                )

            await Promise.all(searchPromises)
            returnData.asyncDataSuccess = true

            $timing.end('catalog-algolia')

            return returnData
        } catch (err) {
            console.log('err', err, store.state.clientIp, route.fullPath, $device.userAgent)
            error({ statusCode: 404, message: 'Invalid URL' })
            return returnData
        }
    },
    data() {
        return {
            isCategoryFilterAB: false,
        }
    },
    head() {
        // We only want to set head data if we have catalogData.
        // If the cat page 404's this still tries to run before it sends them to the error page, so we need to catch it first
        if (!this.catalogData) return {}

        let templateNamePrefix = this.catalogData.type

        const variables = {
            FITMENT: this.fitmentFormatted,
        }

        switch (this.catalogData.type) {
            case 'category':
                if (this.$route.params.facetSlug) templateNamePrefix = 'categoryCustom'

                variables.CATEGORY = this.catalogName
                break

            case 'brand':
                variables.BRAND = this.catalogData.name
                break

            case 'new-products':
                templateNamePrefix = 'newProducts'
                break

            case 'search':
                delete variables.FITMENT
                break
        }

        const template = this.$sitewideData.getBaseMetaTemplates(templateNamePrefix)

        // These metas come from the story if one exists
        const storyblokMetaTemplates = {
            ...(this.catalogData.story?.content.metaTitle && {
                title: this.catalogData.story.content.metaTitle,
            }),
            ...(this.catalogData.story?.content.metaTitleFitment && {
                titleFitment: this.catalogData.story.content.metaTitleFitment,
            }),
            ...(this.catalogData.story?.content.metaDescription && {
                description: this.catalogData.story.content.metaDescription,
            }),
            ...(this.catalogData.story?.content.metaDescriptionFitment && {
                descriptionFitment: this.catalogData.story.content.metaDescriptionFitment,
            }),
            ...(this.catalogData.story?.content.metaDescriptionMake && {
                descriptionMake: this.catalogData.story.content.metaDescriptionMake,
            }),
            ...(this.catalogData.story?.content.metaDescriptionModel && {
                descriptionModel: this.catalogData.story.content.metaDescriptionModel,
            }),
            ...(this.catalogData.story?.content.metaDescriptionYear && {
                descriptionYear: this.catalogData.story.content.metaDescriptionYear,
            }),
        }

        const metas = this.$metaHelpers.generateMetas(
            { ...template, ...storyblokMetaTemplates },
            this.$route,
            variables
        )

        // If we are not on the first page we need to update the title to append the page
        const page = this.$search.catalog.state.currentPage
        if (page > 1) metas.setTitle(`Page ${page} - ${metas.title}`)

        // Page types that we want to be No Index, No Follow
        // Storyblok can also set No Index, No Follow
        if (
            this.$route.name.includes('specials') ||
            this.$route.name.includes('search') ||
            this.catalogData.story?.content.disableSeoIndex === 'true'
        )
            metas.setIsNoIndex()

        // Social Metas
        metas.setSocialUrl(this.$route.fullPath)

        // If we have a social image from the story
        if (this.catalogData.story?.content.shareImage?.filename)
            metas.setSocialImage(
                this.$storyblok.getNormalizedAsset(
                    this.catalogData.story.content.shareImage.filename,
                    1920
                )?.src
            )
        // else if we have results, set the social image to the first result
        else if (this.$search.catalog.state.results.length > 0) {
            const [result] = this.$search.catalog.state.results

            const imageUrl = this.$getImageUrl(
                result.image.key,
                result.image.filename,
                800,
                600,
                80,
                'fff'
            )
            metas.setSocialImage(`https://${this.$sitewideData.domain}${imageUrl}`)
        }

        return metas.format()
    },
    computed: {
        catalogData() {
            return this.$store.state.catalog.data
        },
        catalogName() {
            return this.$route.name.includes('category-custom') && this.$storyblok.isPreview
                ? `${this.catalogData.story?.content?.facetValue || ''} ${this.catalogData.title}`
                : this.catalogData.title
        },
        h1() {
            return this.catalogData.story?.content?.h1 || this.catalogData.h1 || this.catalogName
        },
        disableH1() {
            if (this.catalogData.story) return this.catalogData.story?.content.disableH1 === 'true'

            return false
        },
        hideProducts() {
            if (this.catalogData.story)
                return this.catalogData.story?.content.hideProducts === 'true'

            return false
        },
        hasFullFitment() {
            return this.$store.getters['fitment/hasFullFitment']
        },
        fitmentFormatted() {
            return this.$store.getters['fitment/fitmentFormatted']
        },
        hasRequiredFitmentChanged() {
            return this.$store.state.fitment.hasRequiredFitmentChanged
        },
    },
    watch: {
        async $route(to, from) {
            // Retrigger the asyncData function
            await this.$nuxt.refresh()

            // If our asyncData call wasn't successful then we need to early out of this.
            // No need to run the other things below this.
            if (!this.asyncDataSuccess) return

            // This function will check if the fitment modal needs to open because of the sameDayFacet
            // We don't want to get in a forceOpen loop so we will only try to open the fitment modal
            // when we don't have sameDayShipping in the url and we didn't come from a url will a mod open
            if (!to.query.sameDayShipping && from.query.mod) this.tryFitmentModalOpen()

            this.scrollTop()

            this.emitProductListViewed()
            this.emitProductListFiltered()
        },
    },
    mounted() {
        // Expose a function to allow marketing to AB test
        window.showCategoryFilterAB = (value) => this.showABTest(value)

        // Only run if this is not the first hit and something actually changed
        if (
            this.$sitewideData.config.sameDayShippingEnabled &&
            !this.isFirstHit &&
            this.hasRequiredFitmentChanged
        )
            this.$store.dispatch('fitment/initSelects')

        // This function will check if the fitment modal needs to open because of the sameDayFacet
        this.tryFitmentModalOpen()

        this.emitProductListViewed()
        this.emitProductListFiltered()

        this.$lux.label('Catalog Page')
        this.$lux.pageLoading(false)
    },
    methods: {
        showABTest(value) {
            this.isCategoryFilterAB = value
        },
        tryFitmentModalOpen() {
            if (
                !this.$sitewideData.config.sameDayShippingEnabled ||
                !this.$route.query.sameDayShipping ||
                this.hasFullFitment
            )
                return

            // Open fitment modal in the sameDayShipping mode
            this.$store.dispatch('fitment/showFitmentModal', { isSameDayShippingMode: true })
        },
        scrollTop() {
            // Only scroll if we have a top defined
            if (!this.$refs.scrollTop) return

            // Scrolls to top (top defined by a Ref) so long as we have scrolled passed it.
            const rect = this.$refs.scrollTop.getBoundingClientRect()
            const top = rect.top + window.pageYOffset

            if (window.pageYOffset > top) this.$scrollTo(this.$refs.scrollTop)
        },
        scrollToTopOfResults() {
            const target = this.fitmentFormatted ? '#catalog-title' : '#fitment-inline'
            this.$scrollTo(target, 1)
        },
        emitProductListViewed() {
            const { indexName, queryID, results } = this.$search.catalog.state

            if (results.length === 0) return
            this.$uiEvents.$emit('productListViewed', { results, indexName, queryID })
        },
        emitProductListFiltered() {
            const { indexName, activeRefinements, activeRefinementCount, results } =
                this.$search.catalog.state

            if (activeRefinementCount <= 0 || results.length === 0) return

            this.$uiEvents.$emit('productListFiltered', {
                filters: activeRefinements,
                sort: indexName,
                products: results,
            })
        },
        emitProductListClicked(algoliaObj, position) {
            const { indexName, queryID } = this.$search.catalog.state
            this.$uiEvents.$emit('productListClicked', {
                algoliaObj,
                position,
                indexName,
                queryID,
            })
        },
    },
}
</script>
